import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, LinkSimple, WhatsappLogo, CheckCircle } from "@phosphor-icons/react";
import { myReferralsApi } from "@/lib/api";
import { inviteLink } from "@/components/ShareCardDialog";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

export function InviteCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { myReferralsApi().then(setData).catch(() => {}); }, []);
  useEffect(() => {
    if (data && window.location.hash === "#invite") setTimeout(() => document.getElementById("invite")?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [data]);
  if (!data) return null;
  const link = inviteLink(user);
  const text = `I write all my AI prompts by just talking to SpeakToPrompt. Use my link for a ${data.friend_trial_days}-day free trial: ${link}`;
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); };
  const pct = Math.min(100, (data.weeks_earned / data.max_weeks) * 100);

  return (
    <div id="invite" className="stp-card rounded-3xl p-6 sm:p-8" data-testid="invite-card">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF9F0A] to-[#FF2D55] text-white"><Gift size={20} weight="fill" /></span>
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">Invite friends, get Pro free</h2>
          <p className="mt-1 text-sm text-mist">You get 1 free Pro week when a friend signs up, and 1 more when they pay. Friends get a {data.friend_trial_days}-day trial.</p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        {[["Signed up", data.signups, "ref-signups"], ["Paid", data.paid, "ref-paid"], ["Weeks earned", `${data.weeks_earned}/${data.max_weeks}`, "ref-weeks"]].map(([k, v, t]) => (
          <div key={k} className="rounded-2xl bg-[#FAFAFC] py-4">
            <p data-testid={t} className="font-display text-2xl font-semibold text-ink">{v}</p>
            <p className="text-xs text-mist">{k}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-black/5"><div className="h-full rounded-full bg-gradient-to-r from-[#FF9F0A] to-[#FF2D55]" style={{ width: `${pct}%` }} /></div>
      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <span data-testid="invite-link" className="flex h-11 flex-1 items-center truncate rounded-full bg-[#FAFAFC] px-4 font-mono text-xs text-ink">{link}</span>
        <button data-testid="invite-copy-button" onClick={copy} className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-black/5 px-4 text-sm font-medium text-ink hover:bg-black/10"><LinkSimple size={15} /> Copy</button>
        <button data-testid="invite-whatsapp-button" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer")} className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#25D366] px-4 text-sm font-semibold text-white hover:bg-[#1EBE5A]"><WhatsappLogo size={16} weight="fill" /> WhatsApp</button>
      </div>
      {data.recent.length > 0 && (
        <ul className="mt-5 space-y-1.5" data-testid="invite-recent">
          {data.recent.map((r, i) => (
            <li key={i} className="flex flex-col gap-0.5 text-xs text-mist sm:flex-row sm:items-center sm:justify-between">
              <span className="truncate">{r.email} · {shortDate(r.created_at)}</span>
              {r.paid ? <span className="flex items-center gap-1 text-[#0B6B2A]"><CheckCircle size={12} weight="fill" /> Paid · +{r.weeks_awarded} week{r.weeks_awarded === 1 ? "" : "s"}</span> : <span>Signed up · +{r.weeks_awarded} week{r.weeks_awarded === 1 ? "" : "s"}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
